import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import debounce from 'lodash/debounce';
import type { BorderStyleProps, BorderWidthProps, MinWidthProps } from 'styled-system';
import {
  alignItems,
  borderColor,
  borderStyle,
  borderWidth,
  height,
  justifyContent,
  minWidth,
  themeGet,
  width,
} from 'styled-system';

import { ButtonBase } from '../../components/Button';
import { Flex } from '../../components/Flex';
import Icon, { DEFAULT_ICON_SIZE } from '../../components/Icon';
import { HeadlineH8 } from '../../components/Typography';
import { useLocalization } from '../../providers/LocaleProvider';
import type { BorderColorProps } from '../../theme/system-types';

interface QuantitySelectorProps {
  productName: string;
  maxQuantity: number;
  initialQuantity: number;
  onQuantitySelect: (quantity: number) => void;
  onRemove?: () => void;
  withRemoveOption?: boolean;
}

const DEBOUNCE_MS = 250;

const Container = styled(Flex)<BorderWidthProps & BorderStyleProps & BorderColorProps>`
  ${borderWidth};
  ${borderStyle};
  ${borderColor};
`;

const Text = styled(HeadlineH8)<MinWidthProps>`
  ${minWidth};
`;

const Button = styled(ButtonBase)`
  ${height};
  ${width};
  ${alignItems};
  ${justifyContent};

  position: relative;

  &:before {
    content: '';
    position: absolute;
    width: calc(100% - 2px);
    height: calc(100% - 2px);
    border: 1px solid transparent;
    border-radius: 100%;
  }

  &:after {
    content: '';
    position: absolute;
    width: 48px;
    height: 48px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;

    svg {
      fill: ${themeGet('colors.sys.neutral.icon.inactive')};
    }
  }

  &:hover:not([aria-disabled='true']),
  &:focus-visible {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};

    &:before {
      border-color: ${themeGet('colors.sys.neutral.border.hover')};
    }

    svg {
      fill: ${themeGet('colors.sys.neutral.icon.hover')};
    }
  }

  &:active:not([aria-disabled='true']) {
    background-color: ${themeGet('colors.sys.neutral.background.active')};

    &:before {
      border-color: ${themeGet('colors.sys.neutral.border.active')};
    }

    svg {
      fill: ${themeGet('colors.sys.neutral.icon.active')};
    }
  }
`;

const QuantityButton = props => (
  <Button
    kind="bare"
    width="32px"
    height="32px"
    alignItems="center"
    borderRadius="circle"
    boxSizing="border-box"
    justifyContent="center"
    {...props}
  />
);

const debouncedQuantitySelect = debounce(
  (onQuantitySelect: (quantity: number) => void, quantity: number) => onQuantitySelect(quantity),
  DEBOUNCE_MS
);

export const QuantitySelector = ({
  productName,
  maxQuantity,
  initialQuantity = 1,
  onQuantitySelect,
  onRemove,
  withRemoveOption = true,
  ...props
}: QuantitySelectorProps) => {
  const { r } = useLocalization();

  const [localQuantity, setLocalQuantity] = useState(initialQuantity);

  const handleQuantitySelect = useCallback(
    (quantity: number) => debouncedQuantitySelect(onQuantitySelect, quantity),
    [onQuantitySelect]
  );

  const handleDecrease = (e: MouseEvent) => {
    e.stopPropagation();

    if (localQuantity === 1) {
      onRemove();
    } else if (localQuantity > 0) {
      const newQuantity = localQuantity - 1;
      setLocalQuantity(newQuantity);
      handleQuantitySelect(newQuantity);
    }
  };

  const handleIncrease = (e: MouseEvent) => {
    e.stopPropagation();

    if (localQuantity < maxQuantity) {
      const newQuantity = localQuantity + 1;
      setLocalQuantity(newQuantity);
      handleQuantitySelect(newQuantity);
    }
  };

  const isRemoveShown = withRemoveOption && localQuantity <= 1;
  const isDecreaseDisabled = withRemoveOption ? localQuantity === 0 : localQuantity === 1;

  return (
    <Container
      px="3px"
      height="40px"
      borderWidth="1px"
      alignItems="center"
      borderStyle="solid"
      boxSizing="border-box"
      borderRadius="x-large"
      borderColor="sys.neutral.border.default"
      {...props}
    >
      <QuantityButton
        onClick={(e: MouseEvent) => {
          if (isDecreaseDisabled && !withRemoveOption) {
            return;
          }
          handleDecrease(e);
        }}
        aria-disabled={isDecreaseDisabled}
        aria-label={r.t(isRemoveShown ? 'h24_remove_product' : 'h24_decrease_product_quantity', {
          name: productName,
          quantity: (localQuantity - 1).toString(),
        })}
      >
        {isRemoveShown ? (
          <Icon name="bin" size={DEFAULT_ICON_SIZE} />
        ) : (
          <Icon name="minus" size={DEFAULT_ICON_SIZE} />
        )}
      </QuantityButton>

      <Text mx="sp_4" minWidth="32px" textAlign="center">
        {localQuantity}
      </Text>

      <QuantityButton
        onClick={handleIncrease}
        aria-disabled={localQuantity >= maxQuantity}
        aria-label={r.t('h24_increase_product_quantity', {
          name: productName,
          quantity: localQuantity + 1,
        })}
      >
        <Icon name="plusStrong" size={DEFAULT_ICON_SIZE} />
      </QuantityButton>
    </Container>
  );
};
